import React, { useEffect, useState } from 'react';

// Enhanced Swing for Lanterns
const useSwing = (delay = 0, duration = 3000) => {
  const [style, setStyle] = useState({});
  useEffect(() => {
    const timeout = setTimeout(() => {
      setStyle({
        animation: `lantern-swing ${duration}ms ease-in-out infinite alternate`,
      });
    }, delay);
    return () => clearTimeout(timeout);
  }, [delay, duration]);
  return style;
};

export const Lantern = ({ className = "", size = "md", delay = 0 }) => {
  const style = useSwing(delay);
  const sizeClasses = {
    sm: "w-8 h-10",
    md: "w-12 h-16",
    lg: "w-16 h-20"
  };

  return (
    <div className={`relative group z-20 ${className}`} style={style}>
      {/* Rope */}
      <div className="absolute -top-8 left-1/2 -translate-x-1/2 w-0.5 h-8 bg-gradient-to-b from-yellow-700 to-yellow-500"></div>
      
      {/* Lantern Body */}
      <div className={`${sizeClasses[size] || sizeClasses.md} relative bg-gradient-to-br from-red-600 via-red-500 to-red-700 rounded-2xl shadow-lg shadow-red-500/40 overflow-hidden border border-red-400/30`}>
        {/* Gold Accents Top/Bottom */}
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-yellow-600 via-yellow-400 to-yellow-600"></div>
        <div className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r from-yellow-600 via-yellow-400 to-yellow-600 rounded-b-sm"></div>
        
        {/* Ribs */}
        <div className="absolute inset-0 flex justify-between px-2 opacity-30">
           <div className="w-[1px] h-full bg-red-900 blur-[0.5px]"></div>
           <div className="w-[1px] h-full bg-red-900 blur-[0.5px]"></div>
        </div>

        {/* Center Text (Fu) */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-8 h-8 rounded-full border border-yellow-500/50 flex items-center justify-center bg-red-800/10 backdrop-blur-[1px]">
             <span className="text-yellow-300 font-serif font-bold text-lg drop-shadow-md">福</span>
          </div>
        </div>
        
        {/* Inner Glow */}
        <div className="absolute inset-0 bg-gradient-radial from-yellow-500/20 to-transparent animate-pulse-slow"></div>
      </div>

      {/* Tassel */}
      <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center">
        <div className="w-1.5 h-1.5 rounded-full bg-yellow-500 mb-0.5 shadow-sm"></div>
        <div className="w-1 h-8 bg-red-500/80 shadow-[0_0_2px_rgba(239,68,68,0.8)]"></div>
      </div>
    </div>
  );
};

export const GoldenHorse = ({ className = "" }) => (
  <div className={`relative ${className}`}>
    {/* Glow behind */}
    <div className="absolute inset-0 bg-yellow-500/20 blur-xl rounded-full scale-150 animate-pulse-slow"></div>
    <svg 
      viewBox="0 0 100 100" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-full drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)] filter"
    >
      <defs>
        <linearGradient id="gold-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FCD34D" />
          <stop offset="50%" stopColor="#F59E0B" />
          <stop offset="100%" stopColor="#D97706" />
        </linearGradient>
      </defs>
      {/* Simplified Artistic Horse Silhouette */}
      <path 
        d="M20,70 Q25,65 30,75 T40,80 L45,85 Q50,90 40,90 H30 Q20,90 20,70 Z M30,50 Q40,30 60,30 Q75,30 80,40 Q85,50 80,60 Q75,70 60,65 Q50,60 40,65 Z M60,30 Q65,10 80,10 Q90,10 90,25 Q90,35 80,40" 
        stroke="url(#gold-gradient)" 
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
        className="opacity-90"
      />
      
      {/* Main Body Shape - Abstract/Calligraphic */}
      <path
        d="M25 75 C 30 60, 40 40, 70 35 C 80 33, 85 20, 75 15 C 65 10, 55 15, 60 25 C 65 35, 90 30, 95 45 C 98 55, 90 65, 80 65 C 60 65, 50 85, 40 90 L 30 90"
        fill="url(#gold-gradient)"
        className="drop-shadow-sm"
      />
      
      {/* Mane details */}
       <path d="M65 15 Q 60 5, 70 5" stroke="#FDE047" strokeWidth="2" strokeLinecap="round" />
       
    </svg>
  </div>
);

export const Firework = ({ color = "bg-yellow-400", delay = 0, size = "md", style = {} }) => {
     // A purely CSS animated firework component
    return (
        <div className={`absolute pointer-events-none overflow-hidden ${style.className}`} style={{ left: style.left, top: style.top }}>
            <div className="relative animate-firework-burst">
                {[...Array(8)].map((_, i) => (
                    <div 
                        key={i} 
                        className={`absolute w-1 h-1 rounded-full ${color}`}
                        style={{
                            transform: `rotate(${i * 45}deg) translate(0, 20px)`,
                            opacity: 0,
                            animation: `firework-particle 1.5s ease-out infinite ${delay}s`
                        }}
                    ></div>
                ))}
            </div>
        </div>
    );
}

export const RedEnvelope = ({ className = "" }) => (
    <div className={`w-8 h-10 bg-red-600 rounded-sm border border-red-700 relative shadow-sm rotate-12 hover:rotate-0 transition-transform cursor-pointer ${className}`}>
        <div className="absolute top-2 left-0 right-0 h-[1px] bg-red-800/20"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full border border-yellow-400 bg-yellow-500/20 flex items-center justify-center">
            <span className="text-[8px] text-yellow-300">¥</span>
        </div>
    </div>
)
